import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Mirrors TASK_STATES in @barista/shared. */
export const TASK_STATES = [
  "queued",
  "preparing",
  "running",
  "awaiting_input",
  "awaiting_approval",
  "paused_budget",
  "opening_pr",
  "awaiting_review",
  "addressing_feedback",
  "completed",
  "failed",
  "cancelled",
] as const;

export type TaskState = (typeof TASK_STATES)[number];

/**
 * Every state collapses to one of four tones, so the table, the tiles and the
 * timeline can't disagree about what "bad" looks like.
 */
type Tone = "running" | "waiting" | "good" | "bad" | "idle";

const TONES: Record<TaskState, Tone> = {
  queued: "idle",
  preparing: "running",
  running: "running",
  opening_pr: "running",
  addressing_feedback: "running",
  awaiting_input: "waiting",
  awaiting_approval: "waiting",
  paused_budget: "waiting",
  awaiting_review: "waiting",
  completed: "good",
  failed: "bad",
  cancelled: "idle",
};

const TONE_CLASS: Record<Tone, string> = {
  running: "bg-running/15 text-running border-running/30",
  waiting: "bg-waiting/15 text-waiting border-waiting/30",
  good: "bg-good/15 text-good border-good/30",
  bad: "bg-bad/15 text-bad border-bad/30",
  idle: "bg-edge/50 text-muted border-edge",
};

export function stateClass(state: string): string {
  const tone = TONES[state as TaskState] ?? "idle";
  return TONE_CLASS[tone];
}

const TONE_DOT: Record<Tone, string> = {
  running: "bg-running",
  waiting: "bg-waiting",
  good: "bg-good",
  bad: "bg-bad",
  idle: "bg-muted/50",
};

/** The minimal form of a state: a small colored dot (sidebar rows, lists). */
export function stateDotClass(state: string): string {
  const tone = TONES[state as TaskState] ?? "idle";
  return TONE_DOT[tone];
}

export function stateLabel(state: string): string {
  return state.replaceAll("_", " ");
}

export function money(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return "—";
  return `$${usd.toFixed(2)}`;
}

export function ago(iso: string): string {
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 60) return "just now";
  const units: [number, string][] = [
    [60, "m"],
    [3600, "h"],
    [86400, "d"],
  ];
  for (let i = units.length - 1; i >= 0; i--) {
    const unit = units[i];
    if (!unit) continue;
    const [size, label] = unit;
    if (seconds >= size) return `${Math.floor(seconds / size)}${label} ago`;
  }
  return "just now";
}
